import { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import { useMutation } from '@tanstack/react-query';
import { api } from '../../services/api';
import { PlaceResult } from '../../components/common/PlacesAutocomplete';
import StopListInput from './StopListInput';
import ParamsPanel, { CalcParams } from './ParamsPanel';
import CostBreakdownTable from './CostBreakdownTable';
import { resolveEmptyRunning } from './utils';
import type { PriceEstimateResponse } from '../../types';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK', maximumFractionDigits: 0 }).format(value);

const formatNumber = (value: number, decimals = 1) =>
  new Intl.NumberFormat('da-DK', { maximumFractionDigits: decimals }).format(value);

const defaultParams: CalcParams = {
  trailerId: '',
  driverId: '',
  overrideKm: '',
  overrideHours: '',
  emptyKm: '0',
  emptyHours: '0',
  emptyMode: 'pct',
  calendarDays: '1',
  routeCount: '1',
  marginPct: '15',
};

export default function SimpleCalculator() {
  const [stops, setStops] = useState<(PlaceResult | null)[]>([null, null]);
  const [params, setParams] = useState<CalcParams>(defaultParams);
  const [result, setResult] = useState<PriceEstimateResponse | null>(null);

  const handleParamsChange = (newParams: CalcParams) => {
    setParams(newParams);
    if (newParams.trailerId !== params.trailerId || newParams.driverId !== params.driverId) {
      setResult(null);
    }
  };

  const mutation = useMutation({
    mutationFn: () => {
      const validStops = stops.filter((s): s is PlaceResult => s !== null && s.lat !== undefined && s.lon !== undefined);
      const baseKm = params.overrideKm ? parseFloat(params.overrideKm) : 0;
      const baseHours = params.overrideHours ? parseFloat(params.overrideHours) : 0;
      const empty = resolveEmptyRunning(params, baseKm, baseHours);

      return api.calculatePriceEstimate({
        waypoints: validStops.map(s => ({ lat: s.lat!, lon: s.lon! })),
        trailerId: params.trailerId || undefined,
        driverId: params.driverId || undefined,
        overrideKm: params.overrideKm ? parseFloat(params.overrideKm) : undefined,
        overrideHours: params.overrideHours ? parseFloat(params.overrideHours) : undefined,
        emptyKm: empty.km,
        emptyHours: empty.hours,
        calendarDays: parseInt(params.calendarDays) || 1,
        routeCount: parseInt(params.routeCount) || 1,
        invoiceLineCount: 0,
      });
    },
    onSuccess: (data) => {
      setResult(data);
      if (!params.overrideKm && data.route.googleDistanceKm > 0) {
        setParams(prev => ({ ...prev, overrideKm: String(data.route.googleDistanceKm) }));
      }
      if (!params.overrideHours && data.route.googleDurationHours > 0) {
        setParams(prev => ({ ...prev, overrideHours: String(data.route.googleDurationHours) }));
      }
    },
  });

  const validStopCount = stops.filter(s => s !== null && s.lat !== undefined).length;
  const canCalculate = validStopCount >= 2 || (params.overrideKm && params.overrideHours);

  const marginPct = parseFloat(params.marginPct) || 0;
  const marginAmount = result ? result.totalCost * (marginPct / 100) : 0;
  const sellingPrice = result ? result.totalCost + marginAmount : 0;

  return (
    <Grid container spacing={2}>
      {/* Left column: inputs */}
      <Grid item xs={12} md={5}>
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              Rute
            </Typography>
            <StopListInput stops={stops} onChange={setStops} />
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              Parametre
            </Typography>
            <ParamsPanel
              params={params}
              onChange={handleParamsChange}
              routeInfo={result?.route}
            />
          </CardContent>
        </Card>

        <Button
          variant="contained"
          fullWidth
          startIcon={mutation.isPending ? <CircularProgress size={18} color="inherit" /> : <CalculateIcon />}
          onClick={() => mutation.mutate()}
          disabled={!canCalculate || mutation.isPending}
        >
          {mutation.isPending ? 'Beregner...' : 'Beregn pris'}
        </Button>

        {mutation.isError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {(mutation.error as Error)?.message || 'Beregning fejlede'}
          </Alert>
        )}
      </Grid>

      {/* Right column: results */}
      <Grid item xs={12} md={7}>
        {result ? (
          <>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  Ruteoversigt
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">Afstand</Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {formatNumber(result.route.distanceKm, 1)} km
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">Tid</Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {formatNumber(result.route.durationHours, 1)} timer
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">Total</Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {formatNumber(result.route.totalKm, 1)} km / {formatNumber(result.route.totalHours, 1)} t
                    </Typography>
                  </Grid>
                  {(result.route.emptyKm > 0 || result.route.emptyHours > 0) && (
                    <Grid item xs={12}>
                      <Chip
                        label={`Tomkørsel: ${formatNumber(result.route.emptyKm, 0)} km, ${formatNumber(result.route.emptyHours, 1)} t`}
                        size="small"
                        variant="outlined"
                        color="warning"
                      />
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  Omkostningsberegning
                </Typography>
                <CostBreakdownTable costs={result.costBreakdown} totalCost={result.totalCost} />
              </CardContent>
            </Card>

            <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>Omkostning</Typography>
                  <Typography variant="h6" fontWeight={600}>{formatCurrency(result.totalCost)}</Typography>
                </Box>
                {marginPct > 0 && (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mt: 0.5 }}>
                      <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>Avance ({marginPct}%)</Typography>
                      <Typography variant="h6" fontWeight={600}>+ {formatCurrency(marginAmount)}</Typography>
                    </Box>
                    <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.3)' }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>Salgspris</Typography>
                      <Typography variant="h4" fontWeight={700}>{formatCurrency(sellingPrice)}</Typography>
                    </Box>
                  </>
                )}
                {marginPct === 0 && (
                  <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5 }}>
                    {formatCurrency(result.totalCost)}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 200 }}>
            <Typography color="text.secondary">
              Udfyld rute og parametre, og tryk "Beregn pris"
            </Typography>
          </Box>
        )}
      </Grid>
    </Grid>
  );
}
